import { OperationVariables, useLazyQuery } from "@apollo/client";
import { Model, Q, Query } from "@nozbe/watermelondb";
import { PaginatedList } from "@towersystems/roam-common/lib/shared-types";
import { omit } from "lodash";
import { useEffect, useState } from "react";
import { UseLoadMoreListOptions } from "./types";
import React from "react";

const CONTROLLED_FILTER_KEYS = ["limit", "page"];

const DEFAULT_LIMIT = 14;

export type UseOfflineLoadMoreList<T extends Model> = {
  query: Query<T>;
  limit?: number;
  sortOptions: { [K in keyof T]?: Q.SortOrder };
  initialLoad?: boolean;
};

export const useOfflineLoadMoreList = <T extends Model>({
  query,
  limit: customLimit,
  sortOptions,
  initialLoad,
}: UseOfflineLoadMoreList<T>) => {
  const [loading, setLoading] = useState(initialLoad ? true : false);
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ [K in keyof T]?: Q.SortOrder }>(
    sortOptions || {}
  );
  const [limit] = useState(customLimit || DEFAULT_LIMIT);
  const [totalItems, setTotalItems] = useState(0);

  const loadMore = React.useCallback(
    async (query: Query<T>, shouldReset?: boolean) => {
      const pageLoading = shouldReset ? 1 : page + 1;

      if (
        shouldReset === false &&
        items.length >= 1 &&
        items.length >= totalItems
      ) {
        return;
      }

      setLoading(true);

      const newTotalItems = await query.fetchCount();
      setTotalItems(newTotalItems);

      const qb = query.extend(Q.skip((pageLoading - 1) * limit), Q.take(limit));

      if (sort) {
        Object.keys(sort).forEach((key) => {
          const sortValue = sort[key as keyof T];
          if (sortValue) {
            qb.extend(Q.sortBy(key as any, sortValue));
          }
        });
      }

      return qb
        .fetch()
        .then((res) => {
          setPage(pageLoading);
          setItems((prev) => {
            if (shouldReset) {
              return res;
            }
            return [...prev, ...res];
          });
        })
        .finally(() => setLoading(false));
    },
    [items, page, sort, setPage, setItems]
  );

  useEffect(() => {
    if (initialLoad) {
      loadMore(query, false);
    }
  }, []);

  const reset = () => {
    setPage(0);
    setTotalItems(0);
    setItems([]);
  };

  const response = React.useMemo(() => {
    return {
      items,
      loadMore,
      reset,
      setSort,
      loading,
    };
  }, [items, loadMore, reset, setSort, loading]);

  return response;
};

export const useLoadMoreList = <T>({
  query,
  limit: customLimit,
  resultKey,
  fixedVariables,
  variableFormatter,
  sort: optionsSort = {},
  customResultExtractor,
  initialLoad,
  context,
}: UseLoadMoreListOptions<T>) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ [key: string]: any }>(optionsSort || {});
  const [limit] = useState(customLimit || DEFAULT_LIMIT);
  const [totalItems, setTotalItems] = useState(0);

  const [q, { called }] = useLazyQuery<{
    [key: string]: PaginatedList<T>;
  }>(query, {
    notifyOnNetworkStatusChange: true,
  });

  const loadMore = (variables?: OperationVariables, shouldReset?: boolean) => {
    const pageLoading = shouldReset ? 1 : page + 1;

    if (
      shouldReset === false &&
      items.length >= 1 &&
      items.length >= totalItems
    ) {
      return;
    }

    setLoading(true);
    const v = {
      limit,
      page: pageLoading,
      sort,
      ...omit<any, any>(fixedVariables, CONTROLLED_FILTER_KEYS),
      ...omit<any, any>(variables, CONTROLLED_FILTER_KEYS),
    };

    q({
      context: context,
      variables: variableFormatter ? variableFormatter(v) : v,
    })
      .then((res) => {
        setPage(pageLoading);
        const result = res.data && res.data[resultKey];
        if (result) {
          if (customResultExtractor) {
            let items = customResultExtractor(result);
            setTotalItems(items.length);
            setItems((prev) => {
              if (shouldReset) {
                return [...items];
              }
              return [...prev, ...items];
            });
          } else {
            setTotalItems(result.meta.totalItems);
            setItems((prev) => {
              if (shouldReset) {
                return [...result.items];
              }
              return [...prev, ...result.items];
            });
          }
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (initialLoad) {
      loadMore();
    }
  }, []);

  const reset = () => {
    setPage(0);
    setTotalItems(0);
    setItems([]);
  };

  return {
    called,
    items,
    limit,
    loadMore,
    reset,
    setSort,
    loading,
  };
};
